/**
 * Supabase Client – v7 (Clean & Stable)
 *
 * ROOT CAUSE của các bug auth cũ:
 * ─────────────────────────────────
 * 1. detectSessionInUrl: true mặc định → Supabase parse URL hash/query trước khi
 *    fire INITIAL_SESSION → nếu URL không phải OAuth callback thật → delay/treo.
 * 2. storageKey custom + flowType pkce → PKCE verifier conflict giữa tab/session.
 * 3. Custom storage wrapper phức tạp → JSON.parse fail → session bị xóa bừa.
 *
 * NGUYÊN TẮC v7:
 * ──────────────
 * - detectSessionInUrl: false LUÔN LUÔN ở đây.
 *   AuthCallbackPage tự gọi supabase.auth.exchangeCodeForSession() khi cần.
 * - storageKey cố định 'landscape_auth' → tránh conflict với key mặc định sb-xxx.
 * - Storage wrapper tối giản: chỉ catch lỗi, không validate JSON (Supabase tự làm).
 * - KHÔNG flowType pkce (web SPA không cần, gây bug).
 * - autoRefreshToken: true – Supabase tự refresh, AuthContext catch lỗi.
 *
 * History:
 * v1–v6: Nhiều thay đổi detectSessionInUrl, storageKey, flowType → gây bug
 * v7: Tắt hẳn detectSessionInUrl, callback page tự xử lý → ổn định
 */
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables: VITE_PUBLIC_SUPABASE_URL and VITE_PUBLIC_SUPABASE_ANON_KEY'
  );
}

/** Key lưu session trong localStorage – cố định để tránh conflict */
export const AUTH_STORAGE_KEY = 'landscape_auth';

/** Storage wrapper tối giản – chỉ catch lỗi quota/security */
const safeStorage = {
  getItem: (key: string): string | null => {
    try { return localStorage.getItem(key); } catch { return null; }
  },
  setItem: (key: string, value: string): void => {
    try { localStorage.setItem(key, value); } catch { /* quota exceeded */ }
  },
  removeItem: (key: string): void => {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  },
};

/** Custom fetch wrapper để hỗ trợ Nhập vai thông qua Backend Proxy */
const customFetch = (url: string | URL | Request, options?: Parameters<typeof fetch>[1]) => {
  try {
    const impersonatedId = localStorage.getItem('lscm_impersonated_id');
    if (impersonatedId) {
      const headers = new Headers(options?.headers);
      headers.set('X-Impersonated-User-Id', impersonatedId);
      return fetch(url, { ...options, headers });
    }
  } catch { /* ignore storage errors */ }
  return fetch(url, options);
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  global: { fetch: customFetch },
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    // ⚠️ QUAN TRỌNG: detectSessionInUrl = false
    // AuthCallbackPage tự gọi exchangeCodeForSession() khi URL có code=
    // Tránh Supabase parse URL trên mọi trang → gây delay/treo
    detectSessionInUrl: false,
    storageKey: AUTH_STORAGE_KEY,
    storage: safeStorage,
  },
});

export type SupabaseClient = typeof supabase;
